import { useEffect, useState, useCallback } from "react";
import { Landmark, Wallet, ArrowDownCircle, ArrowUpCircle, Plus, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { formatFCFA, formatDate } from "@/lib/format";
import { Loader } from "@/components/common";
import Modal, { TextField, SelectField } from "@/components/Modal";
import { PageTitle, Panel } from "./AdminShell";
import { toast } from "sonner";

const nameOf = (caisses, key) => (caisses || []).find((c) => c.key === key)?.name || key;

export default function AdminTreasury() {
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState([]);
  const [filter, setFilter] = useState({ account: "", type: "" });
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (filter.account) params.account = filter.account;
    if (filter.type) params.type = filter.type;
    const [s, t] = await Promise.all([api.get("/admin/treasury/summary"), api.get("/admin/treasury", { params })]);
    setSummary(s.data); setTxns(t.data);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette opération ?")) return;
    try { await api.delete(`/admin/treasury/${id}`); toast.success("Opération supprimée"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!summary) return <Loader />;

  return (
    <div>
      <PageTitle action={
        <div className="flex gap-2">
          <button onClick={() => setForm({ type: "entree" })} className="bg-green-600 text-white text-sm font-bold px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="add-entree"><ArrowDownCircle className="w-4 h-4" /> Entrée</button>
          <button onClick={() => setForm({ type: "sortie" })} className="bg-[#C85A32] text-white text-sm font-bold px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="add-sortie"><ArrowUpCircle className="w-4 h-4" /> Sortie</button>
        </div>
      }>Trésorerie</PageTitle>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {(summary.caisses || []).map((c) => (
          <BalanceCard key={c.key} icon={c.key === "electronique" ? Landmark : Wallet} title={c.name} data={c} testid={`card-${c.key}`} />
        ))}
        <div className="rounded-xl p-4 bg-[#0F172A] text-white" data-testid="card-total">
          <p className="text-xs text-slate-300">Solde total</p>
          <p className="font-num font-black text-2xl mt-1">{formatFCFA(summary.total_balance)}</p>
          <p className="text-xs text-slate-400 mt-1">{summary.count} opération(s)</p>
        </div>
      </div>

      <div className="flex gap-2 mb-3 flex-wrap">
        <select value={filter.account} onChange={(e) => setFilter((f) => ({ ...f, account: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="filter-account">
          <option value="">Toutes les caisses</option>{(summary.caisses || []).map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
        </select>
        <select value={filter.type} onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="filter-type">
          <option value="">Entrées & sorties</option><option value="entree">Entrées</option><option value="sortie">Sorties</option>
        </select>
      </div>

      <Panel className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
            <th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Libellé</th>
            <th className="text-left px-4 py-3">Caisse</th><th className="text-right px-4 py-3">Entrée</th>
            <th className="text-right px-4 py-3">Sortie</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {txns.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400">Aucune opération.</td></tr>}
            {txns.map((t) => (
              <tr key={t.id} className="border-t border-slate-100" data-testid={`txn-${t.id}`}>
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(t.date)}</td>
                <td className="px-4 py-3"><p className="font-semibold text-slate-800">{t.label}</p>{t.category && <p className="text-xs text-slate-400">{t.category}</p>}</td>
                <td className="px-4 py-3 text-xs">{nameOf(summary.caisses, t.account)}</td>
                <td className="px-4 py-3 text-right font-num text-green-600">{t.type === "entree" ? formatFCFA(t.amount) : "—"}</td>
                <td className="px-4 py-3 text-right font-num text-[#C85A32]">{t.type === "sortie" ? formatFCFA(t.amount) : "—"}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => remove(t.id)} className="text-slate-300 hover:text-red-500" data-testid={`txn-del-${t.id}`}><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      {form && <TxnModal type={form.type} caisses={summary.caisses || []} onClose={() => setForm(null)} onDone={load} />}
    </div>
  );
}

function BalanceCard({ icon: Icon, title, data, testid }) {
  return (
    <div className="rounded-xl p-4 bg-white border border-slate-200" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-500 text-xs"><Icon className="w-4 h-4" /> {title}</div>
      <p className={`font-num font-black text-2xl mt-1 ${data.balance < 0 ? "text-red-500" : "text-[#0F172A]"}`}>{formatFCFA(data.balance)}</p>
      <p className="text-xs text-slate-400 mt-1">+{formatFCFA(data.in)} · −{formatFCFA(data.out)}</p>
    </div>
  );
}

function TxnModal({ type, caisses, onClose, onDone }) {
  const [f, setF] = useState({ account: (caisses && caisses[0]?.key) || "physique", amount: "", label: "", category: "", date: new Date().toISOString().slice(0, 10) });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!(Number(f.amount) > 0)) return toast.error("Montant invalide");
    try { await api.post("/admin/treasury", { ...f, type, amount: Number(f.amount) }); toast.success("Opération enregistrée"); onDone(); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title={type === "entree" ? "Nouvelle entrée" : "Nouvelle sortie"} onClose={onClose} testid="txn-modal">
      <form onSubmit={submit} className="space-y-3">
        <TextField label="Libellé" value={f.label} onChange={set("label")} testid="txn-label" />
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Montant (FCFA)" type="number" value={f.amount} onChange={set("amount")} testid="txn-amount" />
          <SelectField label="Caisse" value={f.account} onChange={set("account")} testid="txn-account" placeholder={null} options={(caisses || []).map((c) => ({ value: c.key, label: c.name }))} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Catégorie" value={f.category} onChange={set("category")} testid="txn-category" required={false} />
          <TextField label="Date" type="date" value={f.date} onChange={set("date")} testid="txn-date" />
        </div>
        <button className={`w-full text-white font-bold py-3 rounded-lg ${type === "entree" ? "bg-green-600" : "bg-[#C85A32]"}`} data-testid="txn-submit">Enregistrer</button>
      </form>
    </Modal>
  );
}
